/**
 * Human labels for the enum literals the API speaks.
 *
 * The database and the OpenAPI contract use camelCase literals (`partiallyReceived`,
 * `monthlySalary`, `cashHNL`) because they are stable identifiers. Those strings must never reach
 * a person: a manager reading "semiMonthly" in a dropdown is reading our schema, not their
 * business.
 *
 * Two design rules keep this correct and cheap to maintain for a decade:
 *
 * 1. **Grouped, never flat.** A single value→label map would be smaller and would be wrong. The
 *    same literal means different things in different domains — `variant` is both a catalog
 *    target kind and a pricing scope kind; `fixed`, `both`, `none`, `other` and `gross` each
 *    recur in unrelated tables. A flat map silently mislabels one of them, and silence is the
 *    failure mode we can least afford here.
 * 2. **`humanize()` is the fallback, so a gap degrades wording rather than breaking a screen.**
 *    A literal added in 2031 that nobody registers still renders as "Partially received". This
 *    file can never become a source of blocking bugs — which is exactly what makes it safe to
 *    leave incomplete.
 */

/** Acronyms and proper nouns that must survive case conversion intact. */
const PRESERVE = new Set(['USD', 'HNL', 'RTN', 'CAI', 'ISV', 'IHSS', 'RAP', 'KDS', 'POS', 'PDF', 'FX', 'ID']);

/**
 * `partiallyReceived` → `Partially received`. Sentence case, not Title Case: it reads as prose in
 * a table cell, where Title Case On Every Value looks like a machine shouting.
 */
export function humanize(value: string): string {
  const spaced = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  const words = spaced
    .split(/\s+/)
    .map((w) => (PRESERVE.has(w.toUpperCase()) ? w.toUpperCase() : w.toLowerCase()));
  const [first, ...rest] = words;
  if (!first) return value;
  const head = PRESERVE.has(first.toUpperCase()) ? first : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...rest].join(' ');
}

interface EnumEntry {
  label: string;
  /** Mantine colour for badges. Green = settled, red = needs attention, yellow = in flight. */
  color?: string;
  /** Shown under the option in a select, where the label alone leaves a real question open. */
  description?: string;
}

/**
 * Only values whose automatic form would be wrong, ambiguous, or cold need an entry. Resist
 * adding lines that merely restate what `humanize()` already produces — every redundant entry is
 * one more thing to keep in sync with no benefit.
 */
export const ENUMS = {
  language: {
    en: { label: 'English' },
    // In English, not as the endonym. An endonym is right when a person is choosing their OWN
    // language and needs to recognise it; this is an English-only back office where a manager is
    // recording which language SOMEONE ELSE works in, so it should read in the language of the
    // screen it appears on.
    'es-HN': { label: 'Spanish (Honduras)' },
  },
  staffStatus: {
    active: { label: 'Active', color: 'teal' },
    terminated: { label: 'No longer employed', color: 'gray' },
  },
  compensationType: {
    monthlySalary: { label: 'Monthly salary', description: 'A fixed amount each month' },
    hourly: { label: 'Hourly', description: 'Paid for hours actually clocked' },
  },
  payrollCadence: {
    monthly: { label: 'Monthly' },
    biweekly: { label: 'Every two weeks' },
    semiMonthly: { label: 'Twice a month' },
  },
  payrollRunStatus: {
    draft: { label: 'Draft', color: 'yellow' },
    /* "Finalised" is what the column is called; "Signed off" is what the owner did. The colour
       moves too — a register that is signed but not yet paid is not finished, and green said it
       was. */
    finalized: { label: 'Signed off', color: 'blue' },
    paid: { label: 'Paid', color: 'teal' },
  },
  /**
   * How the wages actually left the business.
   *
   * The API takes free text on purpose — how the owner pays is his business and not a list this
   * system gets to close. These are the answers the form offers, so the same words come back every
   * time and a report can group by them. The stored value stays a stable identifier; only the label
   * reaches the screen.
   */
  payrollPaidMethod: {
    bankTransfer: { label: 'Bank transfer' },
    cash: { label: 'Cash' },
    cheque: { label: 'Cheque' },
  },
  timeClockKind: {
    clockIn: { label: 'Clocked in' },
    clockOut: { label: 'Clocked out' },
    breakStart: { label: 'Break started' },
    breakEnd: { label: 'Break ended' },
  },
  statutoryKind: {
    rateBp: { label: 'Percentage rate' },
    floorMinor: { label: 'Minimum amount' },
    ceilingMinor: { label: 'Maximum amount' },
    // Names the UNIT of the value, which is what decides how it is shown and stored. Without this
    // a 480-minute working-day threshold renders as "L 4.80" and typing 480 back would store 48000.
    thresholdMinutes: { label: 'Length of time' },
  },
  currency: {
    HNL: { label: 'Lempiras (L)' },
    USD: { label: 'Dollars ($)' },
  },
  /** The three things a cash box can be. The safe and the float box are not registers. */
  cashBoxKind: {
    drawer: { label: 'Cash drawer', description: 'The money a bartender counts in and out of' },
    // "Cajaita" is what it is called behind the bar, and this is an English-only site — so the
    // label says what it does and the local word stays off the screen.
    cajaita: { label: 'Float box', description: 'Small notes and coins kept back to make change' },
    safe: { label: 'Safe', description: 'Where the night’s takings go until they reach the bank' },
  },
  /**
   * How a drawer is accounted for, which is what decides whose shortage a shortage is.
   *
   * The bar runs `ownerAccountable`: one person counts the drawer in and it is theirs until it is
   * counted off. The other exists so a register can be shared without a shortage belonging to nobody.
   */
  registerSharing: {
    ownerAccountable: {
      label: 'One person is responsible',
      description: 'Whoever counts it in owns whatever it comes up over or short',
    },
    perStaff: {
      label: 'Each person has their own',
      description: 'Everyone serving off this register counts their own money in and out',
    },
  },
  /** Money arriving in or leaving one cash box, on that box's own ledger. */
  cashMovement: {
    transferIn: { label: 'Came in', color: 'teal' },
    transferOut: { label: 'Went out', color: 'orange' },
    bankDeposit: { label: 'To the bank', color: 'blue' },
    // A bill settled straight out of the box — the water man at eleven, paid from the change box —
    // and the reversal that puts the notes back. Box events only; a drawer pays out through its shift.
    paidOut: { label: 'Paid a bill', color: 'orange' },
    paidIn: { label: 'Payment taken back', color: 'teal' },
  },
  /**
   * The cash story's movement vocabulary — summed per kind over a range, on the story cards.
   *
   * Not `cashMovement`: that group names single rows on one box's ledger, and a drop there is just
   * a transfer. The story tells a drop apart because "the nights' takings went into the safe" and
   * "change came over from the safe" are different sentences, and a screen that used one label for
   * both would make a range of honest drops read like unexplained transfers.
   */
  cashStoryMovement: {
    in: { label: 'Came in', color: 'teal' },
    dropIn: { label: 'Dropped in from the drawers', color: 'teal' },
    paidIn: { label: 'Payments put back', color: 'teal' },
    out: { label: 'Went out', color: 'orange' },
    dropOut: { label: 'Dropped to the safe', color: 'orange' },
    paidOut: { label: 'Bills paid out of it', color: 'orange' },
    banked: { label: 'Taken to the bank', color: 'blue' },
  },
  /** Why cash left a drawer for the safe. Both are drops; one of them happens mid-service. */
  safeDrop: {
    nightDrop: { label: 'End of the night' },
    midShift: { label: 'During service' },
  },
  /**
   * A trading night's own state. `reopened` is not the same as `open`: it says this night was
   * declared finished and then opened again, which is the fact an accountant needs and the one a
   * two-value flag would throw away.
   */
  businessDayStatus: {
    open: { label: 'Open', color: 'teal' },
    closed: { label: 'Closed off', color: 'gray' },
    reopened: { label: 'Opened again', color: 'yellow' },
  },
  // Read only by the Devices screen, so these have to answer in the same words its buttons use.
  // "Quarantined" was the odd one out: the button says "Put on hold", the way back says "Put back
  // in service", and the column then replied with a hospital word that sounds like a fifth state —
  // an owner who cannot tell whether he already put the register on hold puts it on hold twice, or
  // retires it instead. "Active" was the raw value with a capital letter for the same reason.
  deviceStatus: {
    // Not "waiting for approval" any more, because there is nothing left to approve: a terminal
    // takes itself into service by being set up, with a code typed into the register. Until then this
    // row is a name and nothing else, and the label should say so rather than imply a pending
    // decision of the owner's.
    pending: { label: 'Not set up yet', color: 'yellow' },
    active: { label: 'In service', color: 'teal' },
    quarantined: { label: 'On hold', color: 'orange' },
    revoked: { label: 'Retired', color: 'red' },
  },
  deviceKind: {
    pos: { label: 'Register' },
    kds: { label: 'Kitchen screen' },
    inventory: { label: 'Stock counter' },
    reports: { label: 'Reports' },
    timeclock: { label: 'Time clock' },
    drawer: { label: 'Cash drawer' },
    portal: { label: 'Office' },
  },
  proposalStatus: {
    // "Waiting for you" read as though this screen were where you answer. Confirmation happens in
    // the conversation with the assistant; the portal only keeps the record.
    pending: { label: 'Not confirmed', color: 'yellow' },
    confirmed: { label: 'Applied', color: 'teal' },
    expired: { label: 'Expired', color: 'gray' },
    rejected: { label: 'Declined', color: 'red' },
  },
  actorType: {
    staff: { label: 'Staff member' },
    portal: { label: 'Office' },
    mcp: { label: 'Assistant' },
    system: { label: 'Automatic' },
    device: { label: 'Register' },
  },
  purchaseOrderStatus: {
    draft: { label: 'Draft', color: 'gray' },
    sent: { label: 'Sent to supplier', color: 'yellow' },
    partiallyReceived: { label: 'Partly received', color: 'yellow' },
    received: { label: 'Received', color: 'teal' },
    cancelled: { label: 'Cancelled', color: 'red' },
  },
  payableStatus: {
    open: { label: 'Unpaid', color: 'yellow' },
    partiallyPaid: { label: 'Part paid', color: 'yellow' },
    paid: { label: 'Paid', color: 'teal' },
    credited: { label: 'Credited', color: 'gray' },
  },
  /**
   * Why a bill and the delivery behind it do not agree.
   *
   * Written as sentences about what the vendor did, not as categories, because the question is put
   * to a manager standing at the door with a piece of paper in his hand — "Quantity variance" is a
   * phrase from an accounting package. Each answer leads somewhere different: a credit note, a
   * conversation, a further bill. That is the whole reason it is asked rather than guessed.
   */
  billDiscrepancyReason: {
    shortDelivery: {
      label: 'They billed for goods that did not arrive',
      description: 'The usual one. Expect a credit note from them',
      color: 'orange',
    },
    overDelivery: {
      label: 'More arrived than they billed for',
      description: 'They will normally bill for the rest later',
      color: 'blue',
    },
    priceDifference: {
      label: 'The price is not the one agreed',
      description: 'The right count, charged at the wrong price',
      color: 'orange',
    },
    unexpectedCharge: {
      label: 'A charge nobody told you about',
      description: 'A delivery fee, a case charge, something added on',
      color: 'orange',
    },
    toQuery: {
      label: 'A mistake, to query with them',
      description: 'Something is wrong and it is not yet clear what',
      color: 'red',
    },
  },
  /** How a disagreement ended, for the endings a credit note does not cover. */
  billDiscrepancyResolution: {
    theySentTheGoods: { label: 'They sent what was missing' },
    agreedAsBilled: { label: 'The bill was right after all' },
    theyWillBillTheRest: { label: 'They will bill for the rest' },
  },
  freightModel: {
    none: { label: 'No freight charge' },
    flatPerOrder: { label: 'Flat fee per order' },
    perCase: { label: 'Per case' },
    perKg: { label: 'Per kilogram' },
    pctOfOrder: { label: 'Percentage of the order' },
  },
  supplierKind: {
    goods: { label: 'Sells goods', description: 'Food, drink, equipment — things you buy' },
    freight: { label: 'Brings goods over', description: 'A ferry or freight company that charges to ship' },
    both: { label: 'Both', description: 'Sells you things and ships them too' },
  },
  freightChargeStatus: {
    draft: { label: 'Draft — not counted yet', color: 'gray' },
    posted: { label: 'Counted in costs', color: 'teal' },
  },
  landedCostAllocation: {
    byValue: { label: 'Split by value' },
    byWeight: { label: 'Split by weight' },
    manual: { label: 'Split by hand' },
  },
  discountKind: {
    percent: { label: 'Percentage off' },
    fixed: { label: 'Fixed amount off' },
    // The ad-hoc price break: the register types the agreed price and the recorded giveaway is the
    // difference. The definition carries no amount of its own, which the pricing page's value
    // column says in words rather than rendering its unused zero as money.
    targetPrice: { label: 'Agreed price, typed at the register' },
  },
  discountStacking: {
    bestOf: { label: 'Best discount wins' },
    stack: { label: 'Discounts add together' },
    exclusive: { label: 'Blocks all other discounts' },
  },
  priceTargetType: {
    menuItem: { label: 'Menu item' },
    // A size. This group has its own `modifierOption` value and the picker that opens on `variant`
    // lists sizes only, so "or option" pointed at the value sitting directly underneath it — the
    // same wrong reading `availabilityTargetType` carried until it was corrected.
    variant: { label: 'Size' },
    modifierOption: { label: 'Modifier' },
    bundle: { label: 'Combo' },
    category: { label: 'Category' },
  },
  scheduleKind: {
    availableDuring: { label: 'Only during these hours' },
    excludedDuring: { label: 'Except during these hours' },
  },
  referralRateKind: {
    percentOfBase: { label: 'Percentage of the bill' },
    fixedPerTicket: { label: 'Fixed amount per ticket' },
  },
  referralBase: {
    preTaxNet: { label: 'Bill before tax' },
    gross: { label: 'Bill including tax' },
  },
  referralPartyKind: {
    agency: { label: 'Agency' },
    driver: { label: 'Driver' },
    independent: { label: 'Independent' },
  },
  /**
   * How often a partner is settled with — and, because a volume tier is counted inside that period,
   * the stretch of trade their rate is worked out over.
   *
   * `biMonthly` is the wire word and it means TWICE A MONTH. The English word is also used for every
   * two months, which is the opposite kind of period, so the label spells out the dates instead of
   * repeating it. Nothing is scheduled off any of these: they name the period, and nobody is paid on
   * a date by anything in this system.
   */
  payoutCadence: {
    weekly: { label: 'Every Friday', description: 'Friday through Thursday, paid the Friday after' },
    biMonthly: {
      label: 'Twice a month',
      description: 'The 1st to the 15th and the 16th to the last, each paid the day after',
    },
    monthly: { label: 'Once a month', description: 'The whole month, paid on the 1st' },
  },
  payoutStatus: {
    payable: { label: 'Ready to pay', color: 'yellow' },
    belowThreshold: { label: 'Under the minimum', color: 'gray' },
    paid: { label: 'Paid', color: 'teal' },
    partiallyPaid: { label: 'Part paid', color: 'yellow' },
  },
  channelKind: {
    inHouse: { label: 'In house' },
    deliveryPlatform: { label: 'Delivery platform' },
  },
  settlementStatus: {
    draft: { label: 'Draft', color: 'gray' },
    reconciled: { label: 'Reconciled', color: 'teal' },
    variance: { label: 'Does not match', color: 'red' },
  },
  documentType: {
    invoice: { label: 'Invoice' },
    creditNote: { label: 'Credit note' },
    debitNote: { label: 'Debit note' },
    depositReceipt: { label: 'Deposit receipt' },
  },
  // Places guests sit. Anything a tab can be opened against.
  spotKind: {
    table: { label: 'Table' },
    barSeat: { label: 'Bar seat' },
    beachChair: { label: 'Beach chair' },
    other: { label: 'Other seating' },
  },
  /**
   * The room itself — drawn on the plan, never assignable. Kept as its own group so the "what is
   * it?" picker can offer seating and scenery separately: nobody scrolling for "Table" should have
   * to pass "Palm tree" on the way.
   */
  spotDecorKind: {
    wall: { label: 'Wall' },
    counter: { label: 'Bar counter' },
    dock: { label: 'Dock' },
    tree: { label: 'Palm or tree' },
    plant: { label: 'Planter' },
    stairs: { label: 'Steps' },
    sign: { label: 'Sign or marker' },
  },
  /**
   * What a dish contains, and what it suits. Every value is listed because the checkboxes on an
   * item are built from this group — an unlisted flag is one nobody can record, and the one a
   * guest asks about is exactly the one that would be missing.
   */
  allergenFlag: {
    gluten: { label: 'Gluten' },
    dairy: { label: 'Dairy' },
    nuts: { label: 'Nuts' },
    shellfish: { label: 'Shellfish' },
    fish: { label: 'Fish' },
    egg: { label: 'Egg' },
    soy: { label: 'Soy' },
    vegan: { label: 'Vegan' },
    vegetarian: { label: 'Vegetarian' },
    spicy: { label: 'Spicy' },
  },
  /**
   * What kind of place a storage area is — never which room it is in. The room is its own field,
   * because the same kinds recur in every one of them and a single column cannot answer both.
   *
   * `bar` is kept so an area still recorded under it reads back as words rather than as a gap, and
   * the storage form filters it out of the choices: it answers the room question, which the room
   * field now answers properly.
   */
  storageKind: {
    shelf: { label: 'Shelf' },
    refrigerator: { label: 'Refrigerator' },
    freezer: { label: 'Deep freezer' },
    underBar: { label: 'Under the bar' },
    prepStation: { label: 'Prep station' },
    floorCrate: { label: 'Floor or crates' },
    walkIn: {
      label: 'Walk-in fridge',
      description: 'Set this when a room gets refrigerated — the place keeps everything counted in it',
    },
    dryStore: { label: 'Dry store' },
    other: { label: 'Somewhere else' },
    bar: { label: 'Bar' },
  },
  /**
   * Which part of the register a menu group is sold from.
   *
   * These are places on a screen, not a description of the goods, which is why they are kept apart
   * from `prepStation` directly below — that one says where an item is MADE, per item, and the two
   * genuinely disagree: a bar snack the kitchen plates belongs on the drinks side and is cooked at
   * the pass, and a shirt is made by nobody at all. The register used to work the second question
   * out from the first and put the shirts in with the burgers.
   *
   * All three values are listed because the dropdown is built from this group, so an unlisted one
   * would be a part of the register nothing could be filed under. `drink` is plural on screen
   * because the owner is choosing a place with a name over it, not describing one glass — and
   * `other` is where the bar's retail is sold, the hookah and cigars and shirts and sunscreen.
   */
  categoryKind: {
    food: { label: 'Food' },
    drink: { label: 'Drinks' },
    other: { label: 'Other' },
  },
  prepStation: {
    kitchen: { label: 'Kitchen' },
    bar: { label: 'Bar' },
    both: { label: 'Kitchen and bar' },
    none: { label: 'No prep needed' },
  },
  course: {
    appetizer: { label: 'Starter' },
    main: { label: 'Main' },
    dessert: { label: 'Dessert' },
    drink: { label: 'Drink' },
    none: { label: 'No course' },
  },
  settlementMode: {
    immediate: { label: 'Paid at the time', description: 'The money reaches you with the sale' },
    onAccount: {
      label: 'Paid out later',
      description: 'The platform holds the money and pays you in a lump sum',
    },
  },
  receivableKind: {
    sale: { label: 'Sale' },
    adjustment: { label: 'Adjustment', color: 'yellow' },
    cancellation: { label: 'Cancelled order', color: 'red' },
  },
  itemMappingStatus: {
    mapped: { label: 'Matched', color: 'teal' },
    reviewQueue: { label: 'Needs matching', color: 'yellow' },
  },
  /**
   * Where a delivery platform's order has got to. Worded from the bar's side of the counter,
   * because that is the only side anyone here can see: the platform's own app tells the guest "on
   * its way", and what this screen has to answer is whether the kitchen still owes somebody a
   * plate. `cancelledByPlatform` says who cancelled it for the same reason — "Cancelled" alone
   * reads as something this bar did, and the first question about one is always whose it was.
   */
  platformOrderStatus: {
    entered: { label: 'Came in', color: 'blue' },
    confirmed: { label: 'Accepted' },
    preparing: { label: 'Being made', color: 'yellow' },
    ready: { label: 'Waiting for the driver', color: 'yellow' },
    handedOff: { label: 'Driver took it', color: 'teal' },
    cancelledByPlatform: { label: 'Cancelled by the platform', color: 'red' },
    rejected: { label: 'Turned down', color: 'red' },
    noShow: { label: 'Driver never came', color: 'orange' },
  },
  /**
   * Whether one line of a platform's order reached the check.
   *
   * "Needs matching" is deliberately the same words the Menu matching tab already uses for a name
   * nobody has matched, because it is the same piece of work — the line is stuck until that name is
   * matched. Two wordings for it would read as two separate problems.
   */
  platformLineStatus: {
    matched: { label: 'Rang up', color: 'teal' },
    needsMatching: { label: 'Needs matching', color: 'orange' },
  },
  /**
   * Why an order from a delivery platform wants a look, in words that say what to do about it.
   *
   * A platform can send more with an order than this system knows how to record — a size or an
   * extra the guest chose, a note for the kitchen, a field of theirs that nothing here reads. All
   * of it is kept exactly as it came, so nothing is lost; the reason these exist at all is that
   * without them nobody would know there was anything to go and read. The last two are money, and
   * are worded as money.
   */
  platformOrderAttention: {
    creditNoteRequired: { label: 'Cancelled sale needs a credit note', color: 'red' },
    taxNeedsReview: { label: 'Platform tax needs review before invoicing', color: 'red' },
    guestChoices: { label: 'The customer chose options', color: 'blue' },
    guestInstruction: { label: 'The customer left a note', color: 'blue' },
    fieldsNotRecorded: { label: 'They sent more than we record', color: 'orange' },
    moneyUnexplained: { label: 'The items do not add up', color: 'red' },
    merchantTipNotBilled: { label: 'A tip for the bar we do not bill', color: 'red' },
  },
  // Stock categories mostly survive humanize() untouched, but every value is listed because the
  // dropdown is built from this group — an unlisted value has no option to offer.
  inventoryCategory: {
    produce: { label: 'Produce' },
    meat: { label: 'Meat' },
    seafood: { label: 'Seafood' },
    liquor: { label: 'Liquor' },
    beer: { label: 'Beer' },
    wine: { label: 'Wine' },
    dryGoods: { label: 'Dry goods' },
    packaging: { label: 'Packaging' },
    cleaning: { label: 'Cleaning supplies' },
    prepared: { label: 'Made in house' },
    other: { label: 'Other' },
  },
  stockMovementType: {
    receive: { label: 'Delivery received', color: 'teal' },
    saleDepletion: { label: 'Sold', color: 'gray' },
    waste: { label: 'Waste', color: 'red' },
    transferIn: { label: 'Moved in', color: 'blue' },
    transferOut: { label: 'Moved out', color: 'blue' },
    countAdjustment: { label: 'Count correction', color: 'yellow' },
    // Three things put stock back and they are one movement: a refund, a send taken back before the
    // kitchen started, and an item struck off under a reason the owner says restocks. The movement
    // carries a note saying which; the type says only that it came back.
    refundRestock: { label: 'Put back on the shelf', color: 'teal' },
    prepConsume: { label: 'Used in prep', color: 'gray' },
    prepProduce: { label: 'Made in prep', color: 'teal' },
    supplierReturn: { label: 'Sent back to the supplier', color: 'gray' },
    remakeDepletion: { label: 'Used on a remake', color: 'red' },
  },
  /**
   * What a sale could not work out. Every one of these is something the owner can go and fix, so
   * the wording says what is missing rather than naming the failure — "no recipe" is a diagnosis,
   * "nothing says what this is made of" is an instruction.
   *
   * All but the last mean no stock moved at all. `noPriceOnFile` is the odd one, and it is here
   * rather than on a list of its own because it is the same conversation reached from the other
   * side: the shelf moved correctly and the MONEY is missing, so a figure the owner reads is not
   * the thing it looks like. Yellow rather than orange — nothing is broken, something is unknown.
   */
  depletionFault: {
    noRecipe: { label: 'Nothing says what it is made of', color: 'orange' },
    ingredientMissing: { label: 'An ingredient is gone', color: 'red' },
    unitNotConvertible: { label: 'The amounts do not match the shelf', color: 'orange' },
    noStorageArea: { label: 'No area to take it from', color: 'orange' },
    recipeCycle: { label: 'A recipe contains itself', color: 'red' },
    noPriceOnFile: { label: 'An ingredient has never been bought', color: 'yellow' },
    // The two the extras bring. Named after what is wrong with the MENU rather than after what the
    // arithmetic did, because both are fixed on the recipes screen and neither is fixed by
    // understanding the depletion.
    modifierNoRecipe: { label: 'A paid extra says nothing about what it uses', color: 'orange' },
    modifierRemovesNothing: { label: 'An extra leaves out something the dish never had', color: 'orange' },
  },
  /**
   * What a pour figure rests on, which decides whether the gap beside it means anything.
   *
   * A variance is only measurable between two counts. Worded as a statement about the COUNTING
   * rather than about the number, because the two absences are not "no result" — they are an
   * instruction to go and count, and the owner is the only person who can carry it out.
   */
  varianceMeasurement: {
    betweenCounts: { label: 'Measured between two counts', color: 'green' },
    noCountInRange: { label: 'Not counted in these dates', color: 'gray' },
    noCountBefore: { label: 'Nothing before it to measure from', color: 'gray' },
  },
  /**
   * How much of "how many can I make" is actually known.
   *
   * `atMost` and `unknown` are the whole point of the column. A recipe the system could not finish
   * working out has to say so, because the alternative reads as a number somebody will act on.
   *
   * `atMost` says that the figure can only come down and deliberately no longer names a cause. There
   * are two: part of a recipe that could not be traced, and a figure only reachable by mixing the
   * options of a choice that reach for one shelf — and "part of the recipe is missing" was plainly
   * false about the second. The row carries which it was, in its fault lines or in the choice named
   * beside the number.
   */
  servingsBasis: {
    complete: { label: 'From the whole recipe' },
    atMost: { label: 'At most — the real figure can only be lower', color: 'orange' },
    unknown: { label: 'Cannot be worked out', color: 'orange' },
  },
  recipeTargetType: {
    variant: { label: 'A dish or drink on the menu' },
    inventoryItem: { label: 'A batch prepped in house' },
    // "Add bacon" is made of bacon in the same sense a burger is made of a patty, so it is written
    // the same way and on the same screen. Worded as what the guest does rather than as "modifier
    // option", which is a word from the schema and not one anybody says at the pass.
    modifierOption: { label: 'An extra someone adds or leaves off' },
  },
  /**
   * Which way a line of an extra's recipe points. Only ever asked on an extra: a dish uses
   * ingredients, it does not hand them back.
   *
   * "Takes it back off" and not "removes" — what a "No cheese" does is cancel the slice the burger
   * would otherwise have used, and it can never take off more than the dish actually had.
   */
  recipeComponentEffect: {
    consumes: { label: 'Uses this' },
    returns: { label: 'Takes this back off' },
  },
  // Kept apart from `priceTargetType` even though the spellings overlap: there `variant` and
  // `category` name what a price attaches to, here they name what a serving window applies to.
  availabilityTargetType: {
    menuItem: { label: 'One item' },
    // A size, and only a size — this column stores a row from the sizes table. "Or option" sent a
    // reader looking for a rule that takes "Add avocado" off at 10am, which this cannot express.
    variant: { label: 'One size' },
    category: { label: 'A whole category' },
    bundle: { label: 'A combo' },
  },
  payoutMethod: {
    cash: { label: 'Cash', description: 'Can be handed over any time' },
    transfer: { label: 'Bank transfer', description: 'Held until they reach their minimum' },
  },
  /**
   * How a supplier's bill was settled. Narrower than the register's tender list on purpose: this is a
   * bill being paid rather than a guest paying, and "Cash" on its own is the wording that gets a
   * drawer count blamed for a supplier payment. Registered here rather than beside the form because
   * the payments list has to read the same values back and the two had already been written twice.
   */
  supplierPaymentMethod: {
    cashDrawer: { label: 'Cash out of the register' },
    // The eleven-in-the-morning payment: no register is open and none is needed. The movement lands on
    // the named box's own ledger, so "Cash" alone would be the drawer-blaming wording again.
    cashBox: { label: 'Cash out of the float box or safe' },
    bankTransfer: { label: 'Bank transfer' },
    other: { label: 'Some other way' },
  },
  bankAccountType: {
    savings: { label: 'Savings account' },
    checking: { label: 'Checking account' },
  },
  idDocumentType: {
    // The card's Spanish name on an English-only site. Unlike RTN it is not a code anybody has to
    // match against something printed, so there is nothing to lose by calling it what it is.
    identidad: { label: 'National ID card' },
    rtn: { label: 'RTN' },
    passport: { label: 'Passport' },
    other: { label: 'Other' },
  },
  // Which side of an agency a commission line belongs to. Kept apart from `referralPartyKind`,
  // which shares two spellings but says what a partner *is* rather than who earned one line.
  referralLevel: {
    driver: { label: 'Driver' },
    agency: { label: 'Agency share' },
  },
  referralEntryKind: {
    accrual: { label: 'Earned', color: 'teal' },
    reversal: { label: 'Taken back', color: 'red' },
    // "Volume bonus" is the word the statement the partner is handed prints. Two names for one row
    // — one on the owner's screen and another on the driver's paper — is an argument at the bar.
    tierAdjustment: { label: 'Volume bonus', color: 'blue' },
  },
  fxRateSource: {
    manual: { label: 'Entered by hand' },
    auto: { label: 'Fetched automatically' },
  },
  roundingDirection: {
    up: { label: 'Always up' },
    nearest: { label: 'To the nearest' },
    down: { label: 'Always down' },
  },
  roundingChangeRule: {
    sameAsTender: { label: 'Same money the guest paid with' },
    baseCurrency: { label: 'Always the house currency' },
  },
  // `both` also appears in `prepStation`, where it means kitchen and bar — which is why these
  // are keyed per group rather than pooled.
  discountScope: {
    line: { label: 'One item' },
    check: { label: 'The whole check' },
    both: { label: 'Either' },
  },
  discountBasis: {
    basePrice: { label: 'The everyday price' },
    effectivePrice: { label: 'The price after any special' },
  },
  /** Why the price check arrived at the figure it shows. */
  priceRuleKind: {
    priceList: { label: 'Taken from a price list' },
    currencySide: { label: 'Priced in this money directly' },
    qtyTier: { label: 'A quantity break applied' },
    adjustment: { label: 'A percentage adjustment applied' },
    discount: { label: 'A discount applied' },
    bestOf: { label: 'The best of several prices was chosen' },
    override: { label: 'A manual override applied' },
  },
  // How a payment was made. Cash is split by currency because the register treats dollars and
  // lempiras as two separate drawers, not two spellings of one thing.
  tenderType: {
    cashUSD: { label: 'Cash (dollars)' },
    cashHNL: { label: 'Cash (lempiras)' },
    card: { label: 'Card' },
    venmo: { label: 'Venmo' },
    zelle: { label: 'Zelle' },
    paypal: { label: 'PayPal' },
    bankTransfer: { label: 'Bank transfer' },
    bitcoin: { label: 'Bitcoin' },
  },
  /**
   * Costs this system records nowhere, named the way the owner would name them.
   *
   * The report publishes the list — so the day one of these becomes data it stops being printed,
   * without a screen going on claiming a hole that has been filled. These are the words for it.
   */
  costNotRecorded: {
    rent: { label: 'Rent' },
    utilities: { label: 'Power, water and internet' },
    cardFees: { label: 'What the card processor keeps' },
    platformCommission: { label: "The delivery platforms' cut" },
    insurance: { label: 'Insurance' },
    licenses: { label: 'Licenses and permits' },
    depreciation: { label: 'Wear on the equipment' },
    ownersTime: { label: 'Your own time' },
  },
  /**
   * Where somebody is standing when a role lets them do something.
   *
   * Kept apart from `deviceKind`, which shares five of these spellings and answers a different
   * question: there `pos` is a piece of hardware you enrol and retire, here it is the counter a
   * server is standing at. Pooling them would have a permission screen headed "Register" where the
   * reader is being told about a place, not a device.
   *
   * `portal` is in the API's surface enum and is deliberately absent here, because nothing may ever
   * be granted against it — staff have no accounts on this site. A row that somehow arrived with
   * that surface is filtered out before it reaches a screen rather than given wording that would
   * promise access nobody has.
   */
  permissionSurface: {
    pos: { label: 'At the register' },
    drawer: { label: 'The cash drawer' },
    kds: { label: 'The kitchen screen' },
    inventory: { label: 'Stock' },
    timeclock: { label: 'Clocking on' },
    reports: { label: 'Figures' },
  },
  /**
   * Doing it yourself and waving somebody else through are two separate grants that never imply
   * one another, and the whole point of the role screen is that you can see which one you ticked.
   * Left as "Perform" and "Approve" the two read as near-synonyms; spelled out, they cannot.
   */
  permissionMode: {
    perform: { label: 'Can do this' },
    approve: { label: 'Can approve someone else doing this' },
  },
  /**
   * Who a customer really is — the link on their record, resolved to a word. Four real links plus
   * the plain guest; the storage words for the dive shop must never reach a screen raw.
   */
  customerLinkKind: {
    owner: { label: 'The owner', color: 'grape' },
    staff: { label: 'Bar staff', color: 'blue' },
    seaHuntStaff: { label: 'Dive shop staff', color: 'teal' },
    seaHuntGuest: { label: 'Dive shop guest', color: 'cyan' },
    guest: { label: 'Guest' },
  },
  /** What a reason code is offered for, when staff have to say why something happened. */
  reasonCodeKind: {
    void: { label: 'Cancelled item', description: 'Taken off the bill before it was paid' },
    comp: { label: 'On the house' },
    remake: { label: 'Made again' },
    waste: { label: 'Thrown away' },
    correction: { label: 'Correction' },
    refund: { label: 'Refund' },
    paidOut: { label: 'Cash paid out of the drawer' },
    depositRefund: { label: 'Deposit returned' },
  },
  /**
   * What a register was doing when it was offline, for the queue of things that did not go through.
   *
   * Written in the INFINITIVE — "Take a payment", not "Payment taken" — because every row wearing
   * one of these labels is a thing that was attempted and refused. Past tense on that screen would
   * tell a manager the opposite of what happened.
   *
   * These are the only values the sync door accepts, so the list is closed rather than open-ended;
   * `RejectedActions.tsx` deliberately does not fall through to `humanize()` for an unregistered
   * one, because the automatic form of `order.lines.add` is our own vocabulary with a capital
   * letter on it.
   */
  offlineAction: {
    'order.open': { label: 'Open a tab' },
    'order.patch': { label: 'Change a tab' },
    'order.lines.add': { label: 'Put items on a tab' },
    'order.line.patch': { label: 'Change an item on a tab' },
    'order.location.assign': { label: 'Seat a tab' },
    'order.fire': { label: 'Send an order to the kitchen' },
    'order.recall': { label: 'Take an order back from the kitchen' },
    'order.line.void': { label: 'Take an item off a bill' },
    // Comps arrive here too — the register applies both through one gesture, and "discount" alone
    // would leave a manager reading a $40 shrimp on the house as a percentage off.
    'order.discount.apply': { label: 'Discount or comp a bill' },
    'order.check.close': { label: 'Close a bill' },
    'payment.take': { label: 'Take a payment' },
    'fiscal.invoice.issue': { label: 'Issue an invoice' },
  },
} as const satisfies Record<string, Record<string, EnumEntry>>;

export type EnumGroup = keyof typeof ENUMS;

function entry(group: EnumGroup, value: unknown): EnumEntry | undefined {
  return (ENUMS[group] as Record<string, EnumEntry>)[String(value)];
}

/** Label for a value in a group. Unregistered values fall back to `humanize()`, never to raw. */
export function enumLabel(group: EnumGroup, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return entry(group, value)?.label ?? humanize(String(value));
}

/** Badge colour, or undefined when the group carries no status meaning. */
export function enumColor(group: EnumGroup, value: unknown): string | undefined {
  return entry(group, value)?.color;
}

/** Every value in a group, as `<Select>` data, in declaration order. */
export function enumOptions(group: EnumGroup) {
  return Object.entries(ENUMS[group] as Record<string, EnumEntry>).map(([value, e]) => ({
    value,
    label: e.label,
    description: e.description,
  }));
}

/**
 * Options for a subset of a group's values — for the several places where the API accepts fewer
 * values than the column allows (a create form offering only the states you can start in).
 */
export function enumOptionsFor(group: EnumGroup, values: readonly string[]) {
  return values.map((value) => ({ value, label: enumLabel(group, value) }));
}
